// Deleting your account (#1136), over #1133's routes: asking, on Your account, and the
// one screen a sign-in during the 14 days is shown.
//
// **A request is not a deletion.** `POST /auth/delete` records it and ends the session;
// the account is deleted at the `deletes_at` the server answers, and signing in before
// then is how it is cancelled. Every date on these screens is the server's, written as
// Your account writes a date; none is worked out here. Before asking, the dialog names
// the date `GET /auth/deletion` says a request made now would get.

import { useEffect, useId, useRef, useState } from "react";

import { ACCOUNT_DELETE_PURPOSE, deletionTimeOf, lastOwnerOf } from "../api/account";
import { messageOf } from "../api/http";
import type { StepUpFactors } from "../api/passkeys";
import { Centre, Notice } from "./chrome";
import {
  StepUpDialog,
  useStepUpAttempt,
  useStepUpFactors,
  type BeginPasskey,
  type StepUpProof,
} from "./StepUp";

/** A failure's words as a sentence of their own: the API's often open lowercase and end bare. */
function sentenceOf(failure: unknown): string {
  const said = messageOf(failure).trim();
  const opened = said.charAt(0).toUpperCase() + said.slice(1);
  return /[.!?…]$/.test(opened) ? opened : `${opened}.`;
}

/** What asking needs of the console: the factors to prove with, and the request itself. */
export interface DeletionRequestActions {
  /** `GET /auth/mfa`, read as the factors a step-up offers. */
  stepUpFactors: () => Promise<StepUpFactors>;
  beginPasskey?: BeginPasskey;
  /** `GET /auth/deletion`'s date for a request made now; absent, the dialog names none. */
  wouldDeleteAt?: () => Promise<string | null>;
  /** Step up, then ask. A success leaves the console at sign-in; a refusal is thrown. */
  request: (proof: StepUpProof) => Promise<void>;
}

/** "Delete account" on Your account: the button, and the dialog it opens. */
export function YourDeletion({ actions }: { actions: DeletionRequestActions }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="panel" id="delete-account" aria-label="Delete your account">
      <div className="panel__head">
        <h2>Delete your account</h2>
      </div>
      <div className="panel__body stack stack--tight">
        <p className="muted">
          Your account is deleted 14 days after you ask. Until then you can sign in and choose
          Cancel deletion to keep it.
        </p>
        <div className="actions">
          <button
            type="button"
            className="btn btn--danger"
            aria-haspopup="dialog"
            onClick={() => setOpen(true)}
          >
            Delete account
          </button>
        </div>
      </div>
      {open ? <DeletionDialog actions={actions} onClose={() => setOpen(false)} /> : null}
    </section>
  );
}

function DeletionDialog({
  actions,
  onClose,
}: {
  actions: DeletionRequestActions;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);
  const title = useId();
  const factors = useStepUpFactors(actions.stepUpFactors);
  // Each attempt spends its proof: a passkey's ceremony token is single-use (#660).
  const { proof, attempt, unproven, setProof, spent } = useStepUpAttempt();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [alone, setAlone] = useState<string[] | null>(null);
  // The date a request made now would get, as the server says it. Null until it answers,
  // and for good if it does not: the dialog then asks in days, and is not held up.
  // Where focus goes when a request is refused: the button pressed is disabled while it
  // is asked, and the proof it sent is spent, so focus would otherwise fall to the page.
  const refusal = useRef<HTMLParagraphElement | null>(null);
  const [refusals, setRefusals] = useState(0);
  useEffect(() => {
    if (refusals > 0) refusal.current?.focus();
  }, [refusals]);
  const [would, setWould] = useState<string | null>(null);
  const readWould = useRef(actions.wouldDeleteAt);
  useEffect(() => {
    let live = true;
    readWould.current?.().then(
      (date) => {
        if (live) setWould(date);
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, []);

  // Into the dialog when it opens, and back to the button that opened it when it closes.
  useEffect(() => {
    const element = dialog.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (element !== null && !element.open) element.showModal?.();
    heading.current?.focus();
    return () => {
      element?.close?.();
      opener?.focus();
    };
  }, []);

  const confirm = async () => {
    if (proof === null) return;
    setProblem(null);
    setAlone(null);
    setBusy(true);
    try {
      await actions.request(proof);
    } catch (failure) {
      const owned = lastOwnerOf(failure);
      if (owned === null) setProblem(`${sentenceOf(failure)} Your account is unchanged.`);
      else setAlone(owned);
      setBusy(false);
      spent();
      setRefusals((n) => n + 1);
    }
  };

  return (
    <dialog
      ref={dialog}
      className="modal"
      aria-labelledby={title}
      data-testid="delete-account"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      // The browser can close the element itself, with no `cancel` to refuse: a second
      // Escape while a request is in flight. The panel's state follows the element, so the
      // button opens a dialog again. Only an element that is closed is followed: the
      // `close` of this dialog's own effect cleanup arrives late, and in a development
      // build after the effect has run again and shown it.
      onClose={(event) => {
        if (!event.currentTarget.open) onClose();
      }}
    >
      <div className="stack stack--tight">
        <h2 id={title} ref={heading} tabIndex={-1}>
          Delete your account?
        </h2>
        {would === null ? (
          <p>
            Your account will be deleted 14 days after you confirm. To keep it, sign in before
            then and choose Cancel deletion. Confirming signs you out, and the sign-in page then
            says the date.
          </p>
        ) : (
          <p>
            If you confirm today, your account will be deleted on {deletionTimeOf(would)}, 14 days from
            now. To keep it, sign in before then and choose Cancel deletion. Confirming signs
            you out.
          </p>
        )}
        <p>
          <strong>What is deleted:</strong> your sign-in, your passkeys, your memberships and your
          profile.
        </p>
        <p>
          <strong>What stays:</strong> everything the workspaces and partners you belong to own. Where
          it names you, it will say “Deleted user”. Your name or address may still appear in these:
        </p>
        {/* Where a name or address outlives the purge: the owner's answers on #1164. */}
        <ul aria-label="What stays">
          <li>
            Emails already sent to other people, such as invitations you sent. Our record of them is
            kept for as long as we keep sent mail.
          </li>
          <li>
            A Jira connection a workspace set up with your address. It stays because the workspace
            chose it.
          </li>
          <li>Your workspaces’ copy of their Slack directory, until the next Slack sync.</li>
          <li>Notes on credit entries written by our staff.</li>
          <li>
            The email Stripe, our payment processor, holds as a workspace’s billing contact, where that
            is your address.
          </li>
        </ul>
        <p>Copies of the audit log already sent to a receiver or exported are not recalled.</p>
        {alone === null ? null : (
          <>
            <Notice tone="bad" focusRef={refusal}>
              You are the last owner of the places listed here, so your account cannot be deleted
              yet. Make someone else an owner of each one first. Your account is unchanged.
            </Notice>
            <ul aria-label="Where you are the last owner">
              {alone.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </>
        )}
        {problem === null ? null : (
          <Notice tone="bad" focusRef={alone === null ? refusal : undefined}>
            {problem}
          </Notice>
        )}
        <StepUpDialog
          key={attempt}
          role="group"
          purpose={ACCOUNT_DELETE_PURPOSE}
          boundTo="deleting your account"
          factors={factors}
          disabled={busy}
          beginPasskey={actions.beginPasskey}
          onProofChange={setProof}
          onError={setProblem}
        />
        <div className="actions">
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy || unproven}
            onClick={() => void confirm()}
          >
            {busy ? "Asking…" : "Delete my account"}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * What a session is shown while its account is being deleted, in place of every page:
 * the date, Cancel deletion and Sign out. `deletesAt` is null where the server has no
 * date to give yet, and the screen then names none.
 */
export function DeletionPending({
  deletesAt,
  onCancel,
  onSignOut,
}: {
  deletesAt: string | null;
  onCancel?: () => Promise<void>;
  onSignOut: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const cancel = async () => {
    if (onCancel === undefined) return;
    setProblem(null);
    setBusy(true);
    try {
      await onCancel();
    } catch (failure) {
      setProblem(`${sentenceOf(failure)} The deletion was not cancelled.`);
      setBusy(false);
    }
  };

  return (
    <Centre title="Your account is being deleted" brandHref={null}>
      <p className="lede">
        {deletesAt === null
          ? "You asked for this account to be deleted, and it has not been deleted yet."
          : `You asked for this account to be deleted. It will be deleted on ${deletionTimeOf(deletesAt)}.`}
      </p>
      <p className="muted">
        Until you cancel, nothing else here can be opened. Cancelling stops the deletion and
        takes you to the page you asked for. Your remembered devices, your other sessions and
        your command-line sign-ins were ended when you asked, and do not come back.
      </p>
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      <div className="actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy}
          onClick={() => void cancel()}
        >
          Cancel deletion
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => void onSignOut()}>
          Sign out
        </button>
      </div>
    </Centre>
  );
}
